import get from 'lodash/get';
import omit from 'lodash/omit';
import { ElementType } from 'react';
import { Form } from 'react-bootstrap';
import { Control, FieldErrors, FieldValues, useController } from 'react-hook-form';
import StarRatings from 'react-star-ratings';
import { ApiError } from '../../../api';
import { ShowFieldErrors } from '../../../utils/forms';
import { FormFeedback } from '../FormFeedback';

interface FormStarRatingFieldProps {
  name: string;
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<FieldValues, any>;
  serverErrors?: ApiError;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  as?: ElementType<any>;
  showServerFieldErrors: ShowFieldErrors;
  setShowServerFieldErrors: (showServerFieldErrors: ShowFieldErrors) => void;
  errors: FieldErrors<FieldValues>;
  extraFieldErrorsToHideOnChange?: string[];
  required?: boolean;
  srOnlyLabel?: boolean;
}

export const FormStarRatingField = ({
  name,
  label,
  serverErrors,
  control,
  showServerFieldErrors,
  setShowServerFieldErrors,
  errors,
  extraFieldErrorsToHideOnChange = [],
  required = false,
  srOnlyLabel = false,
  ...rest
}: FormStarRatingFieldProps & Parameters<typeof StarRatings>[0]) => {
  const { field } = useController({
    name,
    control,
  });
  return (
    <Form.Group className="mb-3" controlId={name}>
      <Form.Label className={`text-primary fw-bold ${srOnlyLabel ? 'visually-hidden' : ''}`}>
        {label}
        {required ? <span className="text-danger"> *</span> : ''}
      </Form.Label>
      <div>
        <StarRatings
          rating={field.value}
          starHoverColor="goldenrod"
          starRatedColor="gold"
          starDimension="30px"
          changeRating={(rating) => {
            setShowServerFieldErrors({
              ...omit(showServerFieldErrors, [name, ...extraFieldErrorsToHideOnChange]),
            });
            field.onChange(rating);
          }}
          numberOfStars={5}
          name={name}
          {...rest}
        />
      </div>
      {showServerFieldErrors?.[name] && (
        <FormFeedback
          clientError={errors[name]}
          serverErrors={get(showServerFieldErrors, name) ? get(serverErrors?.body, name) : null}
        />
      )}
    </Form.Group>
  );
};
